/**
 * Request Coordinator - Central Orchestrator
 *
 * This module provides the central orchestrator for all request processing in the
 * unified API architecture. It manages the complete request lifecycle including
 * retries, authentication, error handling, and metrics integration.
 */

import {
  createDeduplicationManager,
  createDeduplicationKeyFromConfig,
} from './deduplication.manager';
import { createErrorClassifier } from './error.classifier';
import { reportRequestFailure } from './request-failure.reporter';
import {
  createRequestTracker,
  generateRequestId,
  type RequestTracker,
} from './request.tracker';
import { syncMarkEmailUnverified } from '../../../store/store-sync';
import {
  type RequestConfig,
  type RequestContext,
  type DeduplicationKey,
  type RetryEngine,
  type IRequestCoordinator,
  type CoordinatorNotificationEvent,
  type IDeduplicationManager,
  type CoordinatorConfig,
  type RetryCoordinationResult,
  type ErrorClassification,
  type IAuthIntegration,
  type IAxiosIntegrationMetrics,
  RequestStatus,
} from '../../../types/api';
import {
  API_ERROR_CODES,
  isApiError,
  isPaymentRequiredError,
} from '../../../types/api/errors';
import { DEFAULT_COORDINATOR_CONFIG } from '../../../types/api/integration.types';
import {
  NotificationType,
  isAxiosError,
} from '../../../types/api/request.types';
import { isExcludedEndpoint } from '../../../types/api/retry.types';
import { dayjs } from '../../../utils/date';
import { publishPaywall } from '../../paywall/paywall.publisher';
import { publishReadOnly } from '../../read-only/read-only.publisher';
import { readBlobErrorBody, transformAxiosToApiError } from '../errors';
import {
  createCircuitBreaker,
  type CircuitBreaker,
} from '../retry/circuit.breaker';
import { createRetryEngine } from '../retry/retry.engine';

import type { IErrorClassifier, IRequestTracker } from '../../../types/api';
import type { AuthErrorInterface } from '../../../types/api';
import type {
  ApiErrorClass,
  PaymentRequiredError,
} from '../../../types/api/errors';
import type { AxiosInstanceWithOriginal } from '../../../types/api/integration.types';
import type {
  AxiosResponse,
  AxiosInstance,
  AxiosError,
  AxiosRequestConfig,
} from 'axios';

// =============================================================================
// CIRCUIT OPEN ERROR
// =============================================================================

/**
 * Error thrown when the circuit breaker is open for an endpoint and the request
 * is failed fast without hitting the network. It is intentionally NOT routed
 * through retry/error-handling (it is thrown before the request executes), so
 * callers receive an immediate, non-retryable failure.
 */
export class CircuitOpenError extends Error {
  public readonly endpoint: string;
  public readonly isCircuitOpen = true as const;

  constructor(endpoint: string) {
    super(`Circuit breaker is open for ${endpoint}; request rejected`);
    this.name = 'CircuitOpenError';
    this.endpoint = endpoint;
  }
}

// =============================================================================
// REQUEST COORDINATOR IMPLEMENTATION
// =============================================================================

/**
 * Implementation of the RequestCoordinator interface.
 *
 * This class implements the IRequestCoordinator interface from the centralized types,
 * providing the complete request coordination functionality including retries,
 * authentication, error handling, deduplication, and metrics integration.
 */
export class RequestCoordinatorService implements IRequestCoordinator {
  private config: CoordinatorConfig;
  private tracker: IRequestTracker;
  private classifier: IErrorClassifier;
  private deduplicationManager: IDeduplicationManager;
  private retryEngine: RetryEngine;
  private circuitBreaker: CircuitBreaker;
  private axiosClient: AxiosInstance;
  private originalRequest: AxiosInstance['request'];
  private authIntegration?: IAuthIntegration;
  private notificationHandlers: Array<
    (event: CoordinatorNotificationEvent) => void
  > = [];

  /**
   * Cumulative request metrics. The coordinator is the single point through
   * which every request flows, so it owns the canonical counters. The axios
   * integration layer projects these rather than maintaining its own copy,
   * which avoids the two counters drifting (e.g. a retry-recovered request
   * being tallied differently in two places).
   */
  private metrics = {
    totalRequests: 0,
    successfulRequests: 0,
    failedRequests: 0,
    retriedRequests: 0,
  };
  private responseTimes: number[] = [];

  constructor(
    axiosClient: AxiosInstance,
    config: Partial<CoordinatorConfig> = {},
    authIntegration?: IAuthIntegration
  ) {
    this.authIntegration = authIntegration;
    this.config = { ...DEFAULT_COORDINATOR_CONFIG, ...config };
    this.axiosClient = axiosClient;

    // Store original request method to prevent circular request loop
    // When axios client is wrapped by integration layer, originalRequest contains
    // the unwrapped request method that bypasses interceptors
    const clientWithOriginal = axiosClient as AxiosInstanceWithOriginal;
    this.originalRequest =
      clientWithOriginal.originalRequest ??
      (axiosClient.request
        ? axiosClient.request.bind(axiosClient)
        : (((_config: RequestConfig): Promise<never> =>
            Promise.reject(
              new Error('Axios request method not available')
            )) as AxiosInstance['request']));

    this.tracker = createRequestTracker(this.config);
    this.classifier = createErrorClassifier(this.config.retry);
    this.retryEngine = createRetryEngine(this.config.retry);

    // Initialize the circuit breaker. Its `enabled` flag follows the
    // coordinator's enableCircuitBreaker; thresholds come from config (or the
    // breaker's own defaults when unspecified).
    this.circuitBreaker = createCircuitBreaker({
      ...this.config.circuitBreaker,
      enabled: this.config.enableCircuitBreaker,
    });

    // Initialize deduplication manager with coordinator-aware options
    this.deduplicationManager = createDeduplicationManager({
      ...this.config.deduplication,
      enableLogging: this.config.enableLogging,
    });

    // Set up deduplication notifications
    this.deduplicationManager.addNotificationHandler((event) => {
      this.notifyEvent(event);
    });

    // Set up retry engine notifications
    // Note: CoreNotificationEvent and CoordinatorNotificationEvent have compatible
    // string values for type, so we can safely cast between them
    this.retryEngine.addNotificationHandler((event) => {
      this.notifyEvent({
        ...event,
        type: event.type as unknown as NotificationType,
        metadata: event.data,
      });
    });

    // Set up circuit breaker notifications (CoreNotificationEvent -> coordinator)
    this.circuitBreaker.addNotificationHandler((event) => {
      this.notifyEvent({
        ...event,
        type: event.type as unknown as NotificationType,
        metadata: event.data,
      });
    });

    this.setupAxiosIntegration();
  }

  /**
   * Executes a request through the unified coordination system with deduplication
   */
  async executeRequest(config: RequestConfig): Promise<AxiosResponse> {
    // Stamp request metadata + correlation header before anything else so the
    // tracker context id, the deduplication key, and the on-the-wire
    // X-Request-ID all share one identifier for the lifetime of the request.
    this.ensureRequestMetadata(config);

    // Create deduplication key from config
    const deduplicationKey = createDeduplicationKeyFromConfig(config);

    // Whether this call joins a request already on the wire: dedupe shares
    // that one execution, and its one typed error, with every later caller.
    const joinsInFlight = this.deduplicationManager.isPending(deduplicationKey);

    this.metrics.totalRequests++;
    const startTime = dayjs().valueOf();

    try {
      // Execute request with deduplication
      const result = await this.deduplicationManager.execute(
        deduplicationKey,
        () => this.executeRequestInternal(config)
      );

      if (this.config.enableLogging && result.wasDeduplicated) {
        console.log(
          `🔄 Request deduplicated: ${config.method?.toUpperCase() || 'GET'} ${config.url}`,
          {
            cacheKey: result.cacheKey,
            subscribers: result.subscribers,
            responseTime: result.responseTime,
          }
        );
      }

      this.metrics.successfulRequests++;
      this.recordResponseTime(dayjs().valueOf() - startTime);
      return result.data;
    } catch (error) {
      this.metrics.failedRequests++;
      this.recordResponseTime(dayjs().valueOf() - startTime);
      if (joinsInFlight) this.restampSharedRefusal(error, config);
      throw error;
    }
  }

  /**
   * A deduplicated 402 reaches every subscriber as the ONE instance the first
   * caller's request produced, stamped with that caller's send time. A later
   * subscriber is usually a later screen's read (back to Reports while its
   * first read is still in flight), and judged by the first stamp its own
   * refusal would read as sent before the navigation and be dropped by the
   * presenter. So the shared refusal takes its newest asker's send time. The
   * publisher presents on a macrotask, after every subscriber's rejection has
   * run, so the presenter reads the latest stamp.
   */
  private restampSharedRefusal(error: unknown, config: RequestConfig): void {
    if (!isPaymentRequiredError(error)) return;

    const own = config.metadata?.startedAt;
    if (own === undefined) return;

    const shared = error as PaymentRequiredError & {
      requestStartedAt?: number;
    };
    if (
      shared.requestStartedAt === undefined ||
      shared.requestStartedAt >= own
    ) {
      return;
    }

    shared.requestStartedAt = own;
  }

  /**
   * Ensures the request carries coordination metadata and a correlation header.
   * Idempotent: requests that already have metadata (e.g. retry engine sends)
   * are left untouched.
   */
  private ensureRequestMetadata(config: RequestConfig): void {
    if (!config.metadata) {
      config.metadata = {
        requestId: generateRequestId(),
        timestamp: dayjs().valueOf(),
        skipAuth: false,
        retryCount: 0,
        isRetry: false,
      };
    }

    // The retry engine re-stamps `timestamp` per attempt; `startedAt` stays
    // the caller's send time, so a presenter can tell which screen asked.
    config.metadata.startedAt ??= dayjs().valueOf();

    config.headers = {
      ...config.headers,
      'X-Request-ID': config.metadata.requestId,
    };
  }

  /**
   * Computes a rolling average response time over the most recent requests.
   */
  private recordResponseTime(time: number): void {
    this.responseTimes.push(time);
    if (this.responseTimes.length > 100) {
      this.responseTimes = this.responseTimes.slice(-100);
    }
  }

  private getAverageResponseTime(): number {
    if (this.responseTimes.length === 0) return 0;
    const sum = this.responseTimes.reduce((acc, t) => acc + t, 0);
    return sum / this.responseTimes.length;
  }

  /**
   * Internal request execution without deduplication
   */
  private async executeRequestInternal(
    config: RequestConfig
  ): Promise<AxiosResponse> {
    // Create request context
    const context = this.tracker.createRequest(config);

    const endpoint = this.getCircuitEndpoint(config);
    const circuitActive = this.isCircuitBreakerActive(config);

    // Circuit breaker gate: fail fast (without hitting the network) when the
    // circuit for this endpoint is open. Thrown before the try below so it is
    // NOT routed through retry/error handling — callers get an immediate,
    // non-retryable failure.
    if (circuitActive && !this.circuitBreaker.shouldAllow(endpoint)) {
      const circuitError = new CircuitOpenError(endpoint);
      this.tracker.updateRequest(context.id, {
        status: RequestStatus.CIRCUIT_BROKEN,
        lastError: circuitError,
      });
      this.tracker.completeRequest(context.id);
      this.syncCircuitState(endpoint);

      if (this.config.enableLogging) {
        console.log(`⛔ Circuit open for ${endpoint}; failing fast`);
      }

      throw circuitError;
    }

    try {
      this.tracker.updateRequest(context.id, {
        status: RequestStatus.IN_PROGRESS,
      });

      // Execute the request using original (unwrapped) request method
      // to avoid circular loop through interceptors
      const response = await this.originalRequest(config);

      // Handle successful response
      const handledResponse = this.handleResponse(response);

      // Complete the request
      this.tracker.updateRequest(context.id, {
        status: RequestStatus.COMPLETED,
        response: handledResponse,
      });
      this.tracker.completeRequest(context.id);

      if (circuitActive) {
        this.circuitBreaker.recordSuccess(endpoint);
        this.syncCircuitState(endpoint);
      }

      return handledResponse;
    } catch (error) {
      // Handle error through coordination system (classification + retry/auth)
      const result = await this.handleError(error as Error, context);

      if (result.success && result.response) {
        // Recovered (e.g. via retry or auth refresh) — a success for the circuit
        if (circuitActive) {
          this.circuitBreaker.recordSuccess(endpoint);
          this.syncCircuitState(endpoint);
        }
        return result.response;
      }

      // Genuine failure. Only count failures that indicate a service problem
      // (5xx / network / timeout) toward the breaker — never client (4xx)
      // errors, which are not the server's fault.
      if (circuitActive) {
        const classification = this.classifier.classifyError(
          error as Error,
          config
        );
        if (classification.circuitBreakerTrip) {
          this.circuitBreaker.recordFailure(endpoint);
          this.syncCircuitState(endpoint);
        }
      }

      throw result.error || error;
    }
  }

  /**
   * Derives the circuit breaker key for a request: the URL path without the
   * query string, so the same route shares one circuit regardless of params.
   */
  private getCircuitEndpoint(config: RequestConfig): string {
    const url = config.url || 'unknown';
    return url.split('?')[0] ?? url;
  }

  /**
   * Whether the circuit breaker should govern this request. Disabled globally
   * via config, and always skipped for excluded endpoints (e.g. auth routes)
   * so a tripped breaker can never lock users out of logging in.
   */
  private isCircuitBreakerActive(config: RequestConfig): boolean {
    if (!this.config.enableCircuitBreaker) return false;
    return !isExcludedEndpoint(config.url, this.config.retry);
  }

  /**
   * Mirrors the breaker's current state for an endpoint into the request
   * tracker so it surfaces in getMetrics()/performance reporting.
   */
  private syncCircuitState(endpoint: string): void {
    (this.tracker as RequestTracker).updateCircuitBreakerState?.(
      endpoint,
      this.circuitBreaker.getCircuitState(endpoint)
    );
  }

  /**
   * Handles successful responses
   */
  handleResponse(response: AxiosResponse): AxiosResponse {
    // Add response metadata if enabled
    if (this.config.enablePerformanceMonitoring) {
      const requestId = response.config?.metadata?.requestId;
      if (requestId) {
        response.metadata = {
          requestId,
          responseTime: dayjs().valueOf(),
          status: response.status,
          statusText: response.statusText,
          size: this.calculateResponseSize(response),
        };
      }
    }

    return response;
  }

  /**
   * Central error handling with intelligent routing
   */
  async handleError(
    error: Error,
    context: RequestContext
  ): Promise<RetryCoordinationResult> {
    // Classify the error
    const classification = this.classifier.classifyError(error, context.config);

    if (this.config.enableLogging) {
      console.log(`🔍 Error classified for ${context.id}:`, {
        type: classification.type,
        category: classification.category,
        shouldRetry: classification.shouldRetry,
        shouldRefreshAuth: classification.shouldRefreshAuth,
      });
    }

    // Route based on classification
    if (classification.shouldRefreshAuth) {
      return await this.handleAuthError(error, context, classification);
    }

    if (classification.shouldRetry) {
      return await this.handleRetryableError(error, context, classification);
    }

    if (classification.shouldTransform) {
      return await this.handleTransformableError(
        error,
        context,
        classification
      );
    }

    // Default: normalize, mark as failed, and return the typed error
    const transformed = await this.toTypedError(error);
    this.tracker.updateRequest(context.id, {
      status: RequestStatus.FAILED,
      lastError: transformed,
    });
    this.tracker.completeRequest(context.id);

    return {
      success: false,
      error: transformed,
      context,
      metrics:
        context.performanceMetrics || this.createEmptyMetrics(context.id),
    };
  }

  /**
   * Handles authentication errors (401)
   */
  private async handleAuthError(
    error: Error,
    context: RequestContext,
    classification: ErrorClassification
  ): Promise<RetryCoordinationResult> {
    // Suppress unused parameter warning
    void classification;

    // Delegate to AuthIntegration if available
    if (this.authIntegration) {
      const authError = this.toAuthErrorInterface(error);
      const result = await this.authIntegration.handleAuthError(authError);

      if (result.success) {
        // Auth refresh succeeded — replay the original request. originalRequest
        // bypasses the coordinator (no re-entry / no dedup), and the request
        // interceptors (bearer signing) apply the freshly-refreshed token.
        try {
          const response = await this.originalRequest(context.config);
          this.tracker.updateRequest(context.id, {
            status: RequestStatus.COMPLETED,
            response,
          });
          this.tracker.completeRequest(context.id);

          return {
            success: true,
            response,
            context,
            metrics:
              context.performanceMetrics || this.createEmptyMetrics(context.id),
          };
        } catch (replayError) {
          // The replayed request itself failed — normalize and return it.
          const transformed = await this.toTypedError(replayError as Error);
          this.tracker.updateRequest(context.id, {
            status: RequestStatus.FAILED,
            lastError: transformed,
          });
          this.tracker.completeRequest(context.id);

          return {
            success: false,
            error: transformed,
            context,
            metrics:
              context.performanceMetrics || this.createEmptyMetrics(context.id),
          };
        }
      }

      // Auth recovery failed (refresh disabled or failed) — AuthIntegration
      // already fired onAuthError. Normalize the original 401 to a typed error.
      const transformed = await this.toTypedError(error);
      this.tracker.updateRequest(context.id, {
        status: RequestStatus.FAILED,
        lastError: transformed,
      });
      this.tracker.completeRequest(context.id);

      return {
        success: false,
        error: transformed,
        context,
        metrics:
          context.performanceMetrics || this.createEmptyMetrics(context.id),
      };
    }

    // Fallback: no AuthIntegration provided (backward compatibility)
    if (this.config.enableNotifications) {
      this.notifyEvent({
        type: NotificationType.AUTH_REFRESH_REQUIRED,
        requestId: context.id,
        message: 'Authentication refresh required',
        timestamp: dayjs().valueOf(),
      });
    }

    const transformed = await this.toTypedError(error);
    this.tracker.updateRequest(context.id, {
      status: RequestStatus.FAILED,
      lastError: transformed,
    });
    this.tracker.completeRequest(context.id);

    return {
      success: false,
      error: transformed,
      context,
      metrics:
        context.performanceMetrics || this.createEmptyMetrics(context.id),
    };
  }

  /**
   * Handles retryable errors using the retry engine
   * This method delegates retry logic to the dedicated retry engine while
   * preserving the intelligent error classification decisions
   */
  private async handleRetryableError(
    error: Error,
    context: RequestContext,
    classification: ErrorClassification
  ): Promise<RetryCoordinationResult> {
    // Check if classification says we should retry (double-check)
    if (!classification.shouldRetry) {
      const transformed = await this.toTypedError(error);
      this.tracker.updateRequest(context.id, {
        status: RequestStatus.FAILED,
        lastError: transformed,
      });
      this.tracker.completeRequest(context.id);

      return {
        success: false,
        error: transformed,
        context,
        metrics:
          context.performanceMetrics || this.createEmptyMetrics(context.id),
      };
    }

    // Update context with the error and classification data
    const updatedContext = {
      ...context,
      lastError: error,
      status: RequestStatus.RETRYING,
      // Store classification data for the retry engine to use
      classification,
    };

    // Update the tracker with the current error state
    this.tracker.updateRequest(context.id, {
      status: RequestStatus.RETRYING,
      lastError: error,
    });

    // Delegate to the retry engine (it will handle delays using classification data)
    const result = await this.retryEngine.executeRetry(
      updatedContext,
      this.axiosClient
    );

    // Handle the result from retry engine
    if (result.success && result.response) {
      // Success - update tracker and return success result
      this.metrics.retriedRequests++;
      this.tracker.updateRequest(context.id, {
        status: RequestStatus.COMPLETED,
        retryCount: result.context.retryCount,
        response: result.response,
      });
      this.tracker.completeRequest(context.id);

      return {
        success: true,
        response: result.response,
        context: result.context,
        metrics: result.metrics,
      };
    } else {
      // Failure - normalize the final error, update tracker, and return it
      const transformed = await this.toTypedError(result.error || error);
      this.tracker.updateRequest(context.id, {
        status: RequestStatus.FAILED,
        lastError: transformed,
      });
      this.tracker.completeRequest(context.id);

      return {
        success: false,
        error: transformed,
        context: result.context,
        metrics: result.metrics,
      };
    }
  }

  /**
   * Normalize a transformable axios error into the app's typed ApiError so
   * callers receive a consistent, typed error (with .code/.status) rather than
   * a raw AxiosError. Non-axios errors (a thrown Error, CircuitOpenError) and
   * already-typed values pass through untouched. Applied at every non-auth
   * error-return point so the coordinator's output is uniformly typed —
   * including retryable errors that exhaust their retries. The 401 auth path is
   * intentionally excluded (see handleAuthError).
   *
   * transformAxiosToApiError always yields an ApiErrorClass instance (an Error
   * subclass); its declared ApiError return type omits Error's members.
   *
   * Also the paywall trigger (docs/prd/prd-feature-gate-refactor.md, D1):
   * every one of this method's call sites is a FINAL failure exit — including
   * the auth-refresh replay path — so publishing here reaches every 402 that
   * leaves the coordinator, exactly once per physical request (deduplicated
   * subscribers share the one execution, and restamp the shared refusal with
   * their own send time: restampSharedRefusal), without any per-path wiring. The
   * publisher no-ops on everything that is not a `PaymentRequiredError` and
   * never throws, so presenting stays a side effect of a real request failing.
   *
   * The failure reporter rides the same exits for the same reason: one report
   * per physical request, after retries are exhausted (reportRequestFailure).
   *
   * Async for one reason: a blob request (a file export) receives its JSON
   * error body as a Blob, which must be read back before the transform can see
   * the server's message and the paywall its upgrade prompt.
   */
  private async toTypedError(error: Error): Promise<Error> {
    await readBlobErrorBody(error);

    const typed = isAxiosError(error)
      ? (transformAxiosToApiError(error) as ApiErrorClass)
      : error;

    publishPaywall(typed);
    publishReadOnly(typed);
    reportRequestFailure(error, typed);

    // An unverified account is refused almost every signed-in call. The gate
    // each platform shows reads `email_verified`, so a stale user learns it
    // here, from whichever call was refused first.
    if (
      isApiError(typed) &&
      typed.code === API_ERROR_CODES.EMAIL_NOT_VERIFIED
    ) {
      syncMarkEmailUnverified();
    }

    return typed;
  }

  /**
   * Handles errors that need transformation
   */
  private async handleTransformableError(
    error: Error,
    context: RequestContext,
    classification: ErrorClassification
  ): Promise<RetryCoordinationResult> {
    // Suppress unused parameter warning
    void classification;

    const transformed = await this.toTypedError(error);

    this.tracker.updateRequest(context.id, {
      status: RequestStatus.FAILED,
      lastError: transformed,
    });
    this.tracker.completeRequest(context.id);

    return {
      success: false,
      error: transformed,
      context,
      metrics:
        context.performanceMetrics || this.createEmptyMetrics(context.id),
    };
  }

  /**
   * Updates coordinator configuration including deduplication settings
   */
  updateConfig(config: Partial<CoordinatorConfig>): void {
    this.config = { ...this.config, ...config };

    // Update deduplication manager config if deduplication settings changed
    if (
      config.deduplication !== undefined ||
      config.enableLogging !== undefined
    ) {
      this.deduplicationManager.updateConfig({
        ...config.deduplication,
        enableLogging: config.enableLogging ?? this.config.enableLogging,
      });
    }

    // Update circuit breaker config if its settings changed
    if (
      config.circuitBreaker !== undefined ||
      config.enableCircuitBreaker !== undefined
    ) {
      this.circuitBreaker.updateConfig({
        ...config.circuitBreaker,
        enabled: this.config.enableCircuitBreaker,
      });
    }
  }

  /**
   * Gets currently active requests
   */
  getActiveRequests(): RequestContext[] {
    return this.tracker.getActiveRequests();
  }

  /**
   * Gets performance and operational metrics including deduplication stats
   */
  getMetrics(): {
    summary: IAxiosIntegrationMetrics;
    tracker: Record<string, unknown>;
    performance: unknown[];
    circuitBreakers: Map<string, unknown>;
    deduplication: {
      totalRequests: number;
      deduplicatedRequests: number;
      cacheHits: number;
      cacheMisses: number;
      cacheSize: number;
      duplicateSavings: number;
      averageResponseTime: number;
    };
  } {
    return {
      summary: {
        totalRequests: this.metrics.totalRequests,
        successfulRequests: this.metrics.successfulRequests,
        failedRequests: this.metrics.failedRequests,
        retriedRequests: this.metrics.retriedRequests,
        averageResponseTime: this.getAverageResponseTime(),
      },
      tracker: (this.tracker as RequestTracker).getStateStatistics?.() || {},
      performance: this.tracker.getAllPerformanceMetrics(),
      circuitBreakers:
        (this.tracker as RequestTracker).getAllCircuitBreakerStates?.() ||
        new Map(),
      deduplication: this.deduplicationManager.getStats(),
    };
  }

  /**
   * Adds a notification handler
   */
  addNotificationHandler(
    handler: (event: CoordinatorNotificationEvent) => void
  ): void {
    this.notificationHandlers.push(handler);
  }

  /**
   * Removes a notification handler
   */
  removeNotificationHandler(
    handler: (event: CoordinatorNotificationEvent) => void
  ): void {
    const index = this.notificationHandlers.indexOf(handler);
    if (index > -1) {
      this.notificationHandlers.splice(index, 1);
    }
  }

  /**
   * Cleans up resources including deduplication manager
   */
  destroy(): void {
    (this.tracker as RequestTracker).destroy?.();
    this.deduplicationManager.destroy();
    this.circuitBreaker.destroy();
    this.notificationHandlers.length = 0;
  }

  /**
   * Gets deduplication manager for advanced operations
   */
  getDeduplicationManager(): IDeduplicationManager {
    return this.deduplicationManager;
  }

  /**
   * Gets the circuit breaker for advanced operations and inspection.
   */
  getCircuitBreaker(): CircuitBreaker {
    return this.circuitBreaker;
  }

  /**
   * Cancels all pending requests for a given deduplication key
   */
  cancelPendingRequests(key: DeduplicationKey): boolean {
    return this.deduplicationManager.cancel(key);
  }

  /**
   * Checks if a request is currently pending deduplication
   */
  isRequestPending(key: DeduplicationKey): boolean {
    return this.deduplicationManager.isPending(key);
  }

  /**
   * Clears all deduplicated requests from cache
   */
  clearDeduplicationCache(): void {
    this.deduplicationManager.clear();
  }

  /**
   * Resets all circuit breaker state. Primarily for test isolation, where the
   * shared client's breaker would otherwise carry open/failure state across
   * cases.
   */
  resetCircuitBreaker(): void {
    this.circuitBreaker.reset();
  }

  // =============================================================================
  // PRIVATE HELPER METHODS
  // =============================================================================

  private setupAxiosIntegration(): void {
    // The coordinator is integrated through the AxiosIntegration class
    // in axios.integration.ts, not through direct interceptors here.
    // This method is kept for future extensibility but currently does nothing
    // to avoid conflicts with the existing integration layer.
  }

  private calculateResponseSize(response: AxiosResponse): number {
    if (response.data) {
      if (typeof response.data === 'string') {
        return response.data.length;
      }
      if (typeof response.data === 'object') {
        return JSON.stringify(response.data).length;
      }
    }
    return 0;
  }

  private createEmptyMetrics(requestId: string): {
    requestId: string;
    startTime: number;
    retryCount: number;
  } {
    return {
      requestId,
      startTime: dayjs().valueOf(),
      retryCount: 0,
    };
  }

  /**
   * Converts an Error to AuthErrorInterface format expected by AuthIntegration.
   */
  private toAuthErrorInterface(error: Error): AuthErrorInterface {
    const axiosError = error as AxiosError;
    const responseData = axiosError.response?.data as
      Record<string, unknown> | undefined;

    return {
      name: error.name || 'AuthError',
      message: error.message,
      status: axiosError.response?.status || 0,
      response: axiosError.response
        ? {
            status: axiosError.response.status,
            data: {
              message: responseData?.message as string | undefined,
              error: responseData?.error as string | undefined,
              errors: responseData?.errors as
                Record<string, string[]> | undefined,
            },
          }
        : undefined,
      config: axiosError.config as AxiosRequestConfig | undefined,
    };
  }

  private notifyEvent(event: CoordinatorNotificationEvent): void {
    this.notificationHandlers.forEach((handler) => {
      try {
        handler(event);
      } catch (error) {
        console.warn('Notification handler error:', error);
      }
    });
  }
}

// =============================================================================
// FACTORY FUNCTION
// =============================================================================

/**
 * Creates a new request coordinator instance
 */
export function createRequestCoordinator(
  axiosClient: AxiosInstance,
  config?: Partial<CoordinatorConfig>,
  authIntegration?: IAuthIntegration
): IRequestCoordinator {
  return new RequestCoordinatorService(axiosClient, config, authIntegration);
}

// =============================================================================
// INTEGRATION HELPER
// =============================================================================

/**
 * Sets up request coordination for an axios instance
 */
export function setupRequestCoordination(
  axiosClient: AxiosInstance,
  config?: Partial<CoordinatorConfig>
): IRequestCoordinator {
  const coordinator = createRequestCoordinator(axiosClient, config);

  // Replace axios request method with coordinated version
  (axiosClient.request as unknown) = async (
    config: RequestConfig
  ): Promise<AxiosResponse> => {
    return coordinator.executeRequest(config);
  };

  return coordinator;
}
